import React, { useState, useEffect, useRef } from 'react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import {
  FileText, Download, Loader2, TrendingUp, TrendingDown, Users, CreditCard,
  Star, Gift, UserPlus, BarChart2, ArrowRight, Crown, Minus,
} from 'lucide-react';

const PERIODS = [
  { value: 7, label: 'Ultimi 7 giorni' },
  { value: 30, label: 'Ultimi 30 giorni' },
  { value: 90, label: 'Ultimi 90 giorni' },
];

function KpiCard({ label, value, delta, prefix = '', suffix = '' }) {
  const isUp = delta > 0;
  const isDown = delta < 0;
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4">
      <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mb-1">{label}</p>
      <p className="text-2xl font-bold text-[#1A1A1A]">{prefix}{typeof value === 'number' ? value.toLocaleString('it-IT') : value}{suffix}</p>
      {delta !== undefined && delta !== 0 && (
        <div className={`flex items-center gap-1 mt-1 text-[10px] font-semibold ${isUp ? 'text-green-600' : isDown ? 'text-red-500' : 'text-slate-400'}`}>
          {isUp ? <TrendingUp size={10} /> : isDown ? <TrendingDown size={10} /> : <Minus size={10} />}
          {isUp && '+'}{delta.toFixed(1)}% vs periodo prec.
        </div>
      )}
    </div>
  );
}

function TierBar({ name, count, total, color }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs font-medium text-slate-600 w-20 capitalize">{name}</span>
      <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-bold text-[#1A1A1A] w-12 text-right">{count}</span>
    </div>
  );
}

const TIER_COLORS = { bronze: '#CD7F32', silver: '#94A3B8', gold: '#F59E0B', platinum: '#8B5CF6', diamond: '#3B82F6' };

export default function AdminReportBuilder() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef(null);

  const loadReport = async (period) => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/report?days=${period}`);
      setData(r.data);
    } catch { toast.error('Errore generazione report'); }
    setLoading(false);
  };

  useEffect(() => { loadReport(days); }, [days]);

  const exportPDF = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/jpeg', 0.85);
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const imgW = pdfW - 20;
      const imgH = (canvas.height * imgW) / canvas.width;

      let y = 10;
      let remainingH = imgH;
      let srcY = 0;

      while (remainingH > 0) {
        const sliceH = Math.min(pdfH - 20, remainingH);
        const sliceCanvasH = (sliceH / imgH) * canvas.height;

        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceCanvasH;
        const ctx = sliceCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, srcY, canvas.width, sliceCanvasH, 0, 0, canvas.width, sliceCanvasH);

        const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.85);
        if (srcY > 0) pdf.addPage();
        pdf.addImage(sliceData, 'JPEG', 10, 10, imgW, sliceH);

        srcY += sliceCanvasH;
        remainingH -= sliceH;
      }

      pdf.save(`report_${data?.brand_name || 'lazo'}_${days}g.pdf`);
      toast.success('PDF scaricato');
    } catch (e) {
      toast.error('Errore export PDF');
    }
    setExporting(false);
  };

  const exportCSV = async (section) => {
    try {
      const response = await adminApi.get(`/admin/report/csv?days=${days}&section=${section}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `lazo_${section}_${days}g.csv`;
      a.click();
      toast.success('CSV scaricato');
    } catch { toast.error('Errore export CSV'); }
  };

  const k = data?.kpi || {};

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="admin-report-builder">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#1A1A1A]">Report Builder</h1>
          <p className="text-[#64748B] text-sm mt-0.5">Genera un report sintetico da condividere con il reparto marketing</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-100 rounded-xl p-1">
            {PERIODS.map(p => (
              <button key={p.value} onClick={() => setDays(p.value)} data-testid={`period-${p.value}`}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                  days === p.value ? 'bg-white text-[#D44E20] shadow-sm' : 'text-slate-500'
                }`}>{p.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Export buttons */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button onClick={exportPDF} disabled={!data || exporting} data-testid="export-pdf"
          className="flex items-center gap-2 px-5 py-2.5 bg-[#D44E20] text-white text-sm font-semibold rounded-xl hover:bg-[#B8411A] disabled:opacity-40 transition-colors">
          {exporting ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
          {exporting ? 'Generazione...' : 'Scarica PDF'}
        </button>
        <button onClick={() => exportCSV('customers')} disabled={!data} data-testid="export-csv-customers"
          className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-xl hover:bg-slate-50 disabled:opacity-40">
          <Download size={14} /> CSV Clienti
        </button>
        <button onClick={() => exportCSV('transactions')} disabled={!data} data-testid="export-csv-transactions"
          className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-xl hover:bg-slate-50 disabled:opacity-40">
          <Download size={14} /> CSV Transazioni
        </button>
        <button onClick={() => exportCSV('orders')} disabled={!data} data-testid="export-csv-orders"
          className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-xl hover:bg-slate-50 disabled:opacity-40">
          <Download size={14} /> CSV Ordini
        </button>
      </div>

      {loading && (
        <div className="flex justify-center py-16">
          <Loader2 size={32} className="animate-spin text-[#D44E20]" />
        </div>
      )}

      {/* Report preview (this gets exported as PDF) */}
      {data && !loading && (
        <div ref={reportRef} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="report-preview">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#1A1A1A] to-[#2D2D2D] text-white px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">{data.brand_name || 'Report'}</h2>
                <p className="text-white/60 text-sm mt-1">Performance Report — Ultimi {days} giorni</p>
              </div>
              <div className="text-right">
                <p className="text-white/40 text-[10px]">Generato il</p>
                <p className="text-sm font-medium">{new Date(data.generated_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                <p className="text-white/40 text-[10px] mt-1">Powered by Lazo</p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-8">
            {/* Executive KPIs */}
            <div>
              <h3 className="text-sm font-bold text-[#1A1A1A] mb-3 flex items-center gap-2">
                <BarChart2 size={14} className="text-[#D44E20]" /> KPI Principali
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard label="Clienti totali" value={k.total_customers} />
                <KpiCard label="Nuovi clienti" value={k.new_customers} delta={k.new_customers_delta} />
                <KpiCard label="Visite" value={k.total_visits} delta={k.visits_delta} />
                <KpiCard label="Scontrino medio" value={k.avg_ticket} prefix="" suffix=" EUR" />
              </div>
            </div>

            {/* Revenue & Points */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-bold text-[#1A1A1A] mb-3 flex items-center gap-2">
                  <CreditCard size={14} className="text-[#D44E20]" /> Ricavi & Ordini
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <KpiCard label="Ricavi loyalty" value={k.total_revenue} prefix="" suffix=" EUR" delta={k.revenue_delta} />
                  <KpiCard label="Ordini online" value={k.orders_count} />
                  <KpiCard label="Ricavi shop" value={k.orders_revenue} suffix=" EUR" />
                  <KpiCard label="Riscatti premi" value={k.redemptions} />
                </div>
              </div>
              <div>
                <h3 className="text-sm font-bold text-[#1A1A1A] mb-3 flex items-center gap-2">
                  <Star size={14} className="text-[#D44E20]" /> Punti & Referral
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <KpiCard label="Punti emessi" value={k.points_emitted} />
                  <KpiCard label="Punti riscattati" value={k.points_redeemed} />
                  <KpiCard label="Referral totali" value={k.total_referrals} />
                  <KpiCard label="Referral completati" value={k.completed_referrals} />
                </div>
              </div>
            </div>

            {/* Tier distribution */}
            <div>
              <h3 className="text-sm font-bold text-[#1A1A1A] mb-3 flex items-center gap-2">
                <Crown size={14} className="text-[#D44E20]" /> Distribuzione Tier
              </h3>
              <div className="space-y-2 max-w-md">
                {Object.entries(data.tier_distribution || {}).map(([tier, count]) => (
                  <TierBar key={tier} name={tier} count={count} total={k.total_customers} color={TIER_COLORS[tier] || '#94A3B8'} />
                ))}
              </div>
            </div>

            {/* Top customers & Rewards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-bold text-[#1A1A1A] mb-3 flex items-center gap-2">
                  <Users size={14} className="text-[#D44E20]" /> Top 10 Clienti
                </h3>
                <div className="space-y-1">
                  {data.top_customers?.map((c, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-slate-100 text-[10px] font-bold text-slate-500 flex items-center justify-center">{i + 1}</span>
                        <span className="text-xs text-[#1A1A1A] font-medium">{c.name}</span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full capitalize" style={{ backgroundColor: (TIER_COLORS[c.tier] || '#94A3B8') + '18', color: TIER_COLORS[c.tier] || '#94A3B8' }}>{c.tier}</span>
                      </div>
                      <span className="text-xs font-bold text-[#D44E20]">{c.lifetime_points?.toLocaleString('it-IT')} pts</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-bold text-[#1A1A1A] mb-3 flex items-center gap-2">
                  <Gift size={14} className="text-[#D44E20]" /> Premi Piu Riscattati
                </h3>
                <div className="space-y-1">
                  {data.top_rewards?.map((r, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                      <span className="text-xs text-[#1A1A1A]">{r.name}</span>
                      <span className="text-xs font-bold text-slate-600">{r.count}x</span>
                    </div>
                  ))}
                  {(!data.top_rewards || data.top_rewards.length === 0) && <p className="text-xs text-slate-400 italic">Nessun riscatto nel periodo</p>}
                </div>
              </div>
            </div>

            {/* Daily trend mini chart */}
            {data.daily_trend?.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-[#1A1A1A] mb-3 flex items-center gap-2">
                  <TrendingUp size={14} className="text-[#D44E20]" /> Andamento Visite Giornaliere
                </h3>
                <div className="flex items-end gap-0.5 h-24">
                  {data.daily_trend.map((d, i) => {
                    const max = Math.max(...data.daily_trend.map(x => x.visits), 1);
                    const h = (d.visits / max) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${d.date}: ${d.visits} visite`}>
                        <div className="w-full rounded-t transition-all" style={{ height: `${h}%`, backgroundColor: '#D44E20', minHeight: d.visits > 0 ? '2px' : '0px' }} />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-[9px] text-slate-400">{data.daily_trend[0]?.date}</span>
                  <span className="text-[9px] text-slate-400">{data.daily_trend[data.daily_trend.length - 1]?.date}</span>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="border-t border-slate-100 pt-4 flex items-center justify-between">
              <p className="text-[9px] text-slate-300">Report generato da Lazo — lazo.app</p>
              <p className="text-[9px] text-slate-300">Dati aggiornati al {new Date(data.generated_at).toLocaleDateString('it-IT')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
